// WC-V20-HOST-3 — Firefly Messages (card or tab pane).
import { useEffect, useRef, useState } from "react";
import { WorkplaceMessengerBody } from "@/components/messages/WorkplaceMessengerBody";
import { useWorkplaceStreamChatSession } from "@/contexts/WorkplaceStreamChatSessionContext";
import {
  ACTIVITY_DOCK_OPEN_EVENT,
  takePendingDockFocusSection,
  type ActivityDockOpenDetail,
} from "@/components/activity/activityDockOpenEvent";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

export const DOCK_MESSAGES_SECTION_ID = "dock-messages-section";

export type ActivityDockMessagesSectionProps = {
  /** `pane` = tab content (always open, no outer card chrome). */
  presentation?: "card" | "pane";
};

export function ActivityDockMessagesSection({
  presentation = "card",
}: ActivityDockMessagesSectionProps = {}) {
  const session = useWorkplaceStreamChatSession();
  const rootRef = useRef<HTMLElement>(null);
  const isPane = presentation === "pane";
  const [expanded, setExpanded] = useState(isPane || session.unreadCount > 0);

  const focusSelf = () => {
    setExpanded(true);
    window.requestAnimationFrame(() => {
      rootRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  useEffect(() => {
    if (isPane) return;
    if (takePendingDockFocusSection() === "messages") focusSelf();
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<ActivityDockOpenDetail>).detail;
      if (detail?.focusSection === "messages") focusSelf();
    };
    window.addEventListener(ACTIVITY_DOCK_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(ACTIVITY_DOCK_OPEN_EVENT, onOpen);
  }, [isPane]);

  useEffect(() => {
    if (isPane) {
      setExpanded(true);
      return;
    }
    if (session.unreadCount > 0) setExpanded(true);
  }, [isPane, session.unreadCount]);

  if (session.vendorEnabled === false) return null;

  const unread = session.unreadCount;
  const title = unread > 0 ? `Messages · ${unread} unread` : "Messages";

  if (isPane) {
    return (
      <section
        ref={rootRef}
        id={DOCK_MESSAGES_SECTION_ID}
        {...{ [FIREFLY_TARGET_ATTR]: "dock-messages-section" }}
        className="min-h-[200px] text-left text-xs"
        aria-label={title}
      >
        <WorkplaceMessengerBody active compact />
      </section>
    );
  }

  return (
    <section
      ref={rootRef}
      id={DOCK_MESSAGES_SECTION_ID}
      {...{ [FIREFLY_TARGET_ATTR]: "dock-messages-section" }}
      className="mt-3 rounded-2xl border border-teal-200 bg-teal-50/60 px-3 py-3 text-left text-xs dark:border-teal-900 dark:bg-teal-950/25"
      aria-label={title}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-teal-800 dark:text-teal-200">
            {title}
          </p>
          <p className="mt-0.5 text-[11px] text-teal-900/80 dark:text-teal-100/80">
            Team chat stays connected while you switch modes.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="shrink-0 rounded-md border border-teal-300 bg-white px-2 py-1 text-[11px] font-medium text-teal-900 hover:bg-teal-100 dark:border-teal-700 dark:bg-zinc-900 dark:text-teal-100 dark:hover:bg-teal-950/60"
          aria-expanded={expanded}
        >
          {expanded ? "Collapse" : "Open"}
        </button>
      </div>
      {expanded ? (
        <div className="mt-2 min-h-[200px]">
          <WorkplaceMessengerBody active compact />
        </div>
      ) : null}
    </section>
  );
}
